import { get, post, patch, put, httpDelete } from 'api/client';
import { jsonToFormData } from 'utils/formData';
import {
  LegacyMediaLibraryAlbum,
  MediaLibraryAlbum,
  MediaLibraryConfig,
  MediaLibraryImageV1,
  MediaLibraryItems,
  MediaLibrarySettings,
  VideoThumbnail,
} from 'types/MediaLibrary';
import { getExtendedTimeout } from 'utils/request';
import axios, { CancelToken } from 'axios';
import FilesUtil from 'utils/files';
import config from 'config';
import { padStart } from '@kontentino/kontentino-utils/string';
import { Video } from 'types/Video';
import { openBlank } from 'utils/url';
import { Asset, AssetDetail } from 'types/Attachments';
import { AssetStatus } from 'types/AssetStatus';
import { AlbumDetail } from 'app/modules/mediaLibraryPage/types/AlbumDetail';
import {
  AlbumScope,
  isSharedScope,
} from 'app/modules/mediaLibraryPage/utils/albumScope';

const ASSET_POLL_INTERVAL_MS = 2000;

export type AlbumAssignmentFilter =
  | { assignedModelType?: 'page'; assignedModelId?: number }
  | { assignedModelType: 'none'; assignedModelId?: never };

export type AlbumDeleteSummary = {
  sub_albums_count: number;
  sub_album_names: string[];
  files_count: number;
};

export type ListAlbumsParams = AlbumAssignmentFilter & {
  name?: string;
  page?: number;
  limit?: number;
  sortBy?: 'created_at' | 'updated_at' | 'name' | 'file_size';
  sortDirection?: 'asc' | 'desc';
};

export type CreateAlbumParams = {
  name: string;
  assignedModelType?: 'page';
  assignedModelId?: number;
  parentId?: string;
};

export type ListAssetsParams = {
  albumId: string;
  type?: 'images' | 'videos' | 'gifs' | 'documents';
  search?: string;
  createdBy?: number;
  sortBy?: 'created_at' | 'name' | 'file_size';
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
};

export type AssetsListResponse = {
  data: Asset[];
  links: {
    first: string | null;
    last: string | null;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
};

export type UpdateAlbumParams = {
  name?: string;
  description?: string | null;
  cover_image_url?: string | null;
  parent_id?: string | null;
};

export type ArchiveStatus = 'queued' | 'building' | 'ready' | 'failed';

export type Archive = {
  id: string;
  status: ArchiveStatus;
  asset_ids: string[];
  skipped_asset_ids: string[] | null;
  download_url: string | null;
  file_size: number | null;
  expires_at: string;
  failure_reason: string | null;
  created_at: string;
  updated_at: string;
};

export type TrashEntry = {
  id: string;
  type: 'album' | 'asset';
  name: string;
  asset: Asset | null;
  files_count: number;
  sub_albums_count: number;
  deleted_by: { id: number; name: string | null };
  deleted_at: string;
  purge_after: string;
  days_left: number;
};

// The client hands back whatever body came, and an older api sends no id.
export type TrashableDeleteResult =
  | { trash_entry_id?: string }
  | null
  | undefined;

export type TrashRestoreResult = {
  album_id: string | null;
  asset_id: string | null;
  placed: 'original' | 'top_level' | 'main_album';
};

export type TrashListResponse = {
  data: TrashEntry[];
  meta: {
    current_page: number;
    per_page: number;
    total: number;
  };
};

export class AssetProcessingFailedError extends Error {
  constructor(public readonly asset: Asset) {
    super(`Asset ${asset.id} failed processing`);
    this.name = 'AssetProcessingFailedError';
  }
}

function cancelableSleep(ms: number, cancelToken?: CancelToken): Promise<void> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      settled = true;
      resolve();
    }, ms);
    cancelToken?.promise.then((reason) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(reason);
    });
  });
}

type UploadImageParams = {
  file: File;
  albumId?: number;
  cancelToken?: CancelToken;
  onProgress?(progress: number): void;
};

type SimpleUploadAssetParams = {
  file: File;
  cancelToken?: CancelToken;
  onProgress?(progress: number): void;
};

type UploadVideoParams = {
  file: File;
  userId: number;
  pageId?: number;
  albumId?: number;
  userAccountPublicId: string;
  cancelToken: CancelToken;
  onProgress?(progress: number): void;
};

type UploadAssetParams = {
  file: File;
  albumId?: string;
  pageId?: number;
  cancelToken?: CancelToken;
  onProgress?(progress: number): void;
};

type RegisterUploadFile = {
  name: string;
  mime_type: string;
  size: number;
};

type RegisteredUploadPart = {
  url: string;
  part: number;
  size: number;
};

type RegisteredUpload = {
  asset: Asset | null;
  album_id: string | null;
  upload: {
    type: 'normal' | 'multipart';
    id: string | null;
    parts: RegisteredUploadPart[];
  };
  error: string | null;
};

type AssetUploadPart = {
  part: number;
  etag: string;
};

type FinalizeUploadParams = {
  assetId: string;
  albumId: string;
  uploadType: 'normal' | 'multipart';
  uploadId?: string | null;
  parts?: AssetUploadPart[];
};

type GetAlbumMediaParams = {
  albumId: number;
  offset: number;
  type?: string;
};

async function uploadFileToS3SingleShot(
  file: File,
  partUrls: RegisteredUploadPart[],
  cancelToken: CancelToken | undefined,
  reportProgress: (currentPartLoaded: number) => void,
): Promise<AssetUploadPart[]> {
  const [partUrl] = partUrls;
  if (partUrls.length !== 1 || !partUrl) {
    throw new Error(
      `Normal upload plan must have exactly one part URL, got ${partUrls.length}.`,
    );
  }
  await axios.put(partUrl.url, file, {
    cancelToken,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    onUploadProgress: (event: ProgressEvent) => {
      if (event.lengthComputable) reportProgress(event.loaded);
    },
  });
  return [];
}

async function uploadFileToS3InParts(
  file: File,
  partUrls: RegisteredUploadPart[],
  cancelToken: CancelToken | undefined,
  reportProgress: (currentPartLoaded: number) => void,
  markPartCompleted: (loadedBytes: number) => void,
): Promise<AssetUploadPart[]> {
  const expectedTotal = partUrls.reduce((sum, p) => sum + p.size, 0);
  if (expectedTotal !== file.size) {
    throw new Error(
      `Multipart upload plan size mismatch: parts sum to ${expectedTotal} bytes but file is ${file.size} bytes.`,
    );
  }

  const parts: AssetUploadPart[] = [];
  let offset = 0;

  for (let i = 0; i < partUrls.length; i++) {
    const partInfo = partUrls[i];
    const start = offset;
    const end = start + partInfo.size;
    const blob = file.slice(start, end);
    offset = end;

    if (blob.size !== partInfo.size) {
      throw new Error(
        `Multipart part ${partInfo.part} blob size (${blob.size}) does not match expected size (${partInfo.size}). Re-upload the file.`,
      );
    }

    const response = await axios.put(partInfo.url, blob, {
      cancelToken,
      onUploadProgress: (event: ProgressEvent) => {
        if (event.lengthComputable) reportProgress(event.loaded);
      },
    });

    const rawEtag = (response.headers.etag ?? response.headers.ETag) as
      | string
      | undefined;
    if (!rawEtag) {
      // S3 must expose `ETag` via CORS for multipart uploads to work — see
      // bucket CORS `<ExposeHeader>ETag</ExposeHeader>`. Without it, finalize
      // can't be called.
      throw new Error(
        `Missing ETag header on part ${partInfo.part}. Check S3 CORS exposes ETag.`,
      );
    }

    parts.push({ part: partInfo.part, etag: rawEtag.replace(/"/g, '') });
    markPartCompleted(blob.size);
  }

  return parts;
}

export const MediaApi = {
  uploadImage({
    file,
    albumId,
    cancelToken,
    onProgress,
  }: UploadImageParams): Promise<Record<string, MediaLibraryImageV1>> {
    const data = new FormData();

    if (albumId) {
      data.append('album_id', albumId.toString());
    }

    data.append('medialibrary_files_photos[0]', file);

    return post('/gallery/ajaxJustUpload', data, {
      timeout: getExtendedTimeout(2),
      cancelToken,
      onUploadProgress: (progressEvent: ProgressEvent) => {
        if (progressEvent.lengthComputable && !!onProgress) {
          onProgress(
            Math.ceil((progressEvent.loaded / progressEvent.total) * 100),
          );
        }
      },
    });
  },
  async uploadVideo(params: UploadVideoParams) {
    function startPhase(): Promise<{ sessionId: number }> {
      const data = new FormData();

      data.append('userId', params.userId.toString());
      data.append('targetFileSize', params.file.size.toString());
      data.append('filenameWithExtension', params.file.name);
      data.append('userAccountPublicId', params.userAccountPublicId.toString());

      if (params.pageId) {
        data.append('pageId', params.pageId.toString());
      }

      if (params.albumId) {
        data.append('albumId', params.albumId.toString());
      }

      return post(`/videos/create-video-upload-session `, data, {
        cancelToken: params.cancelToken,
        baseURL: config.MEDIA_UPLOAD_SERVICE_ENDPOINT,
      });
    }

    async function transferPhase(sessionId: number) {
      const chunks = FilesUtil.splitFileToChunks(params.file);

      for (let i = 0; i < chunks.length; i++) {
        const partName = padStart(i, 5, '0');
        const fileChunk = new File([chunks[i]], partName);
        const data = new FormData();

        data.append('sessionId', sessionId.toString());
        data.append('chunk', fileChunk);
        data.append('partName', partName);

        const percentagesByChunk = 100 / chunks.length;

        await post(`/videos/transfer-video-chunk`, data, {
          cancelToken: params.cancelToken,
          baseURL: config.MEDIA_UPLOAD_SERVICE_ENDPOINT,
          timeout: getExtendedTimeout(10),
          onUploadProgress: (progressEvent) => {
            if (progressEvent.lengthComputable && !!params.onProgress) {
              const chunkProgress =
                (progressEvent.loaded / progressEvent.total) *
                  percentagesByChunk +
                i * percentagesByChunk;

              params.onProgress(Math.ceil(chunkProgress));
            }
          },
        });

        params.onProgress?.(Math.ceil(percentagesByChunk * (i + 1)));
      }
    }

    function finishPhase(sessionId: number): Promise<{ videoId: number }> {
      const data = new FormData();
      data.append('sessionId', sessionId.toString());

      params.onProgress?.(100);

      return post(`/videos/complete-video-and-upload-to-cloud`, data, {
        cancelToken: params.cancelToken,
        baseURL: config.MEDIA_UPLOAD_SERVICE_ENDPOINT,
        timeout: getExtendedTimeout(10),
      });
    }

    const { sessionId } = await startPhase();
    await transferPhase(sessionId);
    const finishPhaseResponse = await finishPhase(sessionId);

    if (finishPhaseResponse?.videoId) {
      return { videoId: finishPhaseResponse.videoId };
    }

    throw new Error('Can not get video ID');
  },
  getVideo(id: number): Promise<Video> {
    return get('/videos/ajaxGetVideo', {
      params: { id },
    });
  },
  getAlbumMedia: ({
    albumId,
    offset,
    type,
  }: GetAlbumMediaParams): Promise<MediaLibraryItems> => {
    return get(`/albums/ajaxGetAlbumMediaAndVideos`, {
      params: {
        offset,
        type,
        album_id: albumId,
      },
    });
  },
  deleteMedium(id: number | string): Promise<void> {
    return post(`gallery/ajaxRemoveMedium`, jsonToFormData({ id }));
  },
  getAlbums(): Promise<LegacyMediaLibraryAlbum[]> {
    return get(`/albums/ajaxGetAlbums`);
  },
  uploadVideoThumbnail(data: {
    videoId: number;
    thumbnail: File;
    thumbnailOffsetMilliseconds?: number;
  }): Promise<VideoThumbnail> {
    const requestData: {
      video_id: number;
      'medialibrary_files_photos[0]': File;
      thumbnail_offset_milliseconds?: number;
    } = {
      video_id: data.videoId,
      'medialibrary_files_photos[0]': data.thumbnail,
    };

    if (data.thumbnailOffsetMilliseconds) {
      requestData['thumbnail_offset_milliseconds'] =
        data.thumbnailOffsetMilliseconds;
    }

    return post(
      '/Posts/ajaxUploadVideoThumbnail',
      jsonToFormData(requestData),
      {
        timeout: getExtendedTimeout(3),
      },
    );
  },
  removeVideoThumbnail(videoId: number) {
    return post(
      '/Posts/ajaxRemoveVideoThumbnail',
      jsonToFormData({ video_id: videoId }),
    );
  },
  getAsset(
    id: string,
    options: { cancelToken?: CancelToken } = {},
  ): Promise<Asset> {
    return get(`/media-library/assets/${id}`, {
      baseURL: config.API_SERVICE_ENDPOINT,
      cancelToken: options.cancelToken,
    });
  },
  getAssetDetail(id: string): Promise<AssetDetail> {
    return get(`/media-library/assets/${id}`, {
      baseURL: config.API_SERVICE_ENDPOINT,
      params: { include: 'user_id,albums,uploaded_by' },
    });
  },
  async pollAssetUntilReady(
    assetId: string,
    options: { cancelToken?: CancelToken; intervalMs?: number } = {},
  ): Promise<Asset> {
    const interval = options.intervalMs ?? ASSET_POLL_INTERVAL_MS;
    while (true) {
      options.cancelToken?.throwIfRequested();

      const asset = await MediaApi.getAsset(assetId, {
        cancelToken: options.cancelToken,
      });

      if (asset.status === AssetStatus.READY) return asset;
      if (asset.status === AssetStatus.FAILED) {
        throw new AssetProcessingFailedError(asset);
      }

      await cancelableSleep(interval, options.cancelToken);
    }
  },
  registerUpload(params: {
    albumId?: string;
    pageId?: number;
    files: RegisterUploadFile[];
  }): Promise<RegisteredUpload[]> {
    return post(
      '/media-library/assets/register-upload',
      {
        album_id: params.albumId,
        page_id: params.albumId ? undefined : params.pageId,
        files: params.files,
      },
      { baseURL: config.API_SERVICE_ENDPOINT },
    );
  },
  finalizeUpload(params: FinalizeUploadParams): Promise<Asset> {
    return post(
      '/media-library/assets/finalize-upload',
      {
        asset_id: params.assetId,
        album_id: params.albumId,
        upload_type: params.uploadType,
        upload_id: params.uploadId ?? null,
        parts: params.parts ?? null,
      },
      { baseURL: config.API_SERVICE_ENDPOINT },
    );
  },
  simpleUploadAsset({
    file,
    cancelToken,
    onProgress,
  }: SimpleUploadAssetParams): Promise<Asset> {
    const data = new FormData();

    data.append('file', file);

    return post('/media-library/assets/upload', data, {
      baseURL: config.API_SERVICE_ENDPOINT,
      timeout: getExtendedTimeout(2),
      cancelToken,
      onUploadProgress: (progressEvent: ProgressEvent) => {
        if (progressEvent.lengthComputable && !!onProgress) {
          onProgress(
            Math.ceil((progressEvent.loaded / progressEvent.total) * 100),
          );
        }
      },
    });
  },
  async uploadAsset({
    file,
    albumId,
    pageId,
    cancelToken,
    onProgress,
  }: UploadAssetParams): Promise<Asset> {
    const [registered] = await MediaApi.registerUpload({
      albumId,
      pageId,
      files: [{ name: file.name, mime_type: file.type, size: file.size }],
    });

    if (
      !registered ||
      registered.error ||
      !registered.asset ||
      !registered.album_id
    ) {
      throw new Error(registered?.error ?? 'Failed to register upload.');
    }

    const totalSize = file.size;
    let completedBytes = 0;

    const reportProgress = (currentPartLoaded: number) => {
      if (!onProgress || totalSize === 0) return;
      onProgress(
        Math.min(
          100,
          Math.ceil(((completedBytes + currentPartLoaded) / totalSize) * 100),
        ),
      );
    };

    const parts: AssetUploadPart[] =
      registered.upload.type === 'multipart'
        ? await uploadFileToS3InParts(
            file,
            registered.upload.parts,
            cancelToken,
            reportProgress,
            (loaded) => {
              completedBytes += loaded;
            },
          )
        : await uploadFileToS3SingleShot(
            file,
            registered.upload.parts,
            cancelToken,
            reportProgress,
          );

    return MediaApi.finalizeUpload({
      assetId: registered.asset.id,
      albumId: registered.album_id,
      uploadType: registered.upload.type,
      uploadId: registered.upload.id,
      parts: registered.upload.type === 'multipart' ? parts : undefined,
    });
  },
  downloadMedium(params: { id: string | number; type: 'image' | 'file' }) {
    const url =
      params.type === 'image'
        ? `/posts2/downloadMedium?id=${params.id}`
        : `/media/ajaxDownloadFileMedium?id=${params.id}`;

    openBlank(url);
  },
  async listAlbums(
    params: ListAlbumsParams = {},
  ): Promise<MediaLibraryAlbum[]> {
    const response = await get<
      { data: MediaLibraryAlbum[] } | MediaLibraryAlbum[]
    >('/media-library/albums', {
      baseURL: config.API_SERVICE_ENDPOINT,
      params: {
        name: params.name,
        assigned_model_type: params.assignedModelType,
        assigned_model_id: params.assignedModelId,
        page: params.page,
        limit: params.limit,
        sort_by: params.sortBy,
        sort_direction: params.sortDirection,
        include: 'user_id',
      },
    });
    return Array.isArray(response) ? response : response.data;
  },
  createAlbum(params: CreateAlbumParams): Promise<MediaLibraryAlbum> {
    return post(
      '/media-library/albums',
      {
        name: params.name,
        assigned_model_type: params.assignedModelType,
        assigned_model_id: params.assignedModelId,
        parent_id: params.parentId,
      },
      { baseURL: config.API_SERVICE_ENDPOINT },
    );
  },
  getAlbumDetail(albumId: string): Promise<AlbumDetail> {
    return get<AlbumDetail>(`/media-library/albums/${albumId}`, {
      baseURL: config.API_SERVICE_ENDPOINT,
      params: {
        include: 'breadcrumbs,children,user_id,assigned_model,at_nesting_limit',
      },
    });
  },
  getAlbumTree(params: AlbumAssignmentFilter): Promise<MediaLibraryAlbum[]> {
    return get<MediaLibraryAlbum[]>('/media-library/albums/tree', {
      baseURL: config.API_SERVICE_ENDPOINT,
      params: {
        assigned_model_type: params.assignedModelType,
        assigned_model_id: params.assignedModelId,
      },
    });
  },
  getAlbumDeleteSummary(albumId: string): Promise<AlbumDeleteSummary> {
    return get<AlbumDeleteSummary>(
      `/media-library/albums/${albumId}/delete-summary`,
      { baseURL: config.API_SERVICE_ENDPOINT },
    );
  },
  listAssets(params: ListAssetsParams): Promise<AssetsListResponse> {
    return get<AssetsListResponse>('/media-library/assets', {
      baseURL: config.API_SERVICE_ENDPOINT,
      params: {
        album_id: [params.albumId],
        type: params.type,
        name: params.search,
        created_by: params.createdBy,
        sort_by: params.sortBy,
        sort_direction: params.sortDirection,
        page: params.page,
        limit: params.limit,
        include: 'user_id',
      },
    });
  },
  renameAsset(assetId: string, fileName: string): Promise<Asset> {
    return patch<Asset>(
      `/media-library/assets/${assetId}/rename`,
      { name: fileName },
      { baseURL: config.API_SERVICE_ENDPOINT },
    );
  },
  deleteAsset(assetId: string): Promise<TrashableDeleteResult> {
    return httpDelete(`/media-library/assets/${assetId}`, {
      baseURL: config.API_SERVICE_ENDPOINT,
    });
  },
  updateAlbum(
    albumId: string,
    params: UpdateAlbumParams,
  ): Promise<MediaLibraryAlbum> {
    return patch<MediaLibraryAlbum>(
      `/media-library/albums/${albumId}`,
      params,
      { baseURL: config.API_SERVICE_ENDPOINT },
    );
  },
  deleteAlbum(albumId: string): Promise<TrashableDeleteResult> {
    return httpDelete(`/media-library/albums/${albumId}`, {
      baseURL: config.API_SERVICE_ENDPOINT,
    });
  },
  attachAssetsToAlbum(albumId: string, assetIds: string[]): Promise<void> {
    return post(
      `/media-library/albums/${albumId}/attach-assets`,
      { asset_ids: assetIds },
      { baseURL: config.API_SERVICE_ENDPOINT },
    );
  },
  detachAssetsFromAlbum(albumId: string, assetIds: string[]): Promise<void> {
    return post(
      `/media-library/albums/${albumId}/detach-assets`,
      { asset_ids: assetIds },
      { baseURL: config.API_SERVICE_ENDPOINT },
    );
  },
  setAlbumAsMaster(albumId: string): Promise<MediaLibraryAlbum> {
    return post<MediaLibraryAlbum>(
      `/media-library/albums/${albumId}/set-as-master`,
      {},
      { baseURL: config.API_SERVICE_ENDPOINT },
    );
  },
  createArchive(assetIds: string[]): Promise<Archive> {
    return post<Archive>(
      '/media-library/archives',
      { asset_ids: assetIds },
      { baseURL: config.API_SERVICE_ENDPOINT },
    );
  },
  getArchive(archiveId: string): Promise<Archive> {
    return get<Archive>(`/media-library/archives/${archiveId}`, {
      baseURL: config.API_SERVICE_ENDPOINT,
    });
  },
  listTrash(params: {
    scope: AlbumScope;
    page: number;
    limit: number;
  }): Promise<TrashListResponse> {
    return get<TrashListResponse>('/media-library/trash', {
      baseURL: config.API_SERVICE_ENDPOINT,
      params: {
        ...(isSharedScope(params.scope)
          ? { shared: 1 }
          : { page_id: params.scope }),
        page: params.page,
        limit: params.limit,
      },
    });
  },
  restoreTrashEntry(id: string): Promise<TrashRestoreResult> {
    return post<TrashRestoreResult>(
      `/media-library/trash/${id}/restore`,
      {},
      { baseURL: config.API_SERVICE_ENDPOINT },
    );
  },
  deleteTrashEntryNow(id: string): Promise<{ files_kept: number }> {
    return httpDelete<{ files_kept: number }>(`/media-library/trash/${id}`, {
      baseURL: config.API_SERVICE_ENDPOINT,
    });
  },
  getConfig(): Promise<MediaLibraryConfig> {
    return get<MediaLibraryConfig>('/media-library/config', {
      baseURL: config.API_SERVICE_ENDPOINT,
    });
  },
  getSettings(): Promise<MediaLibrarySettings> {
    return get<MediaLibrarySettings>('/media-library/settings', {
      baseURL: config.API_SERVICE_ENDPOINT,
    });
  },
  updateSettings(params: MediaLibrarySettings): Promise<MediaLibrarySettings> {
    return put<MediaLibrarySettings>('/media-library/settings', params, {
      baseURL: config.API_SERVICE_ENDPOINT,
    });
  },
};
